import { GridColDef } from '@mui/x-data-grid';
import { MoreVert } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Tooltip,
} from '@mui/material';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import LockIconOutlined from '@mui/icons-material/LockOutlined';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { Row } from '../interfaces';
import useDeletePlan from './useDeletePlan';
import useGenerateXls from '../../Plan/PlanSummary/hooks/useGenerateXls';
import CountryStatus from '../CountryStatus/CountryStatus';
import usePlanStore from '../../../../../store/planStore';
import statuses from '../helpers/statuses';
import { useAuthContext } from '../../../../../context/AuthContext';
import getAbilityForAccepting from '../../Plan/PlanSummary/helpers/getAbilityForAccepting';

type AcceptUnlockRequest = {
  id: string;
  status: string; // statuses.BOOKED | statuses.ACCEPTED
  action: 'accept' | 'revert';
};

type Options = {
  onAcceptUnlockRequest: (payload: AcceptUnlockRequest) => void;
};

const useMediaPlansTableColumns = (
  setOpenDeleteModal: Dispatch<SetStateAction<boolean>>,
  opts: Options
) => {
  const { onAcceptUnlockRequest } = opts;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [deletedId, setDeletedId] = useState<string | null>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { comparedPlans, setComparedPlan, removeComparedPlan } = usePlanStore();
  const { userRoles } = useAuthContext();

  const { deletePlan } = useDeletePlan();
  const { generateXls } = useGenerateXls();

  const handleAddToComparision = (rowId: string, planName: string) => {
    setComparedPlan(rowId, { planName });
  };

  const handleRemoveFromComparision = (rowId: string) => {
    removeComparedPlan(rowId);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    rowId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleView = (id: string) => {
    navigate(`/media-planning/accepted/${id}`);
    handleMenuClose();
  };

  // Instead of firing the mutation here, request opening modal in parent
  const handleAcceptPlan = (id: string) => {
    onAcceptUnlockRequest({ id, status: statuses.BOOKED, action: 'accept' });
    handleMenuClose();
  };

  const handleUnlockPlan = (id: string) => {
    onAcceptUnlockRequest({ id, status: statuses.ACCEPTED, action: 'revert' });
    handleMenuClose();
  };

  const handleEdit = (id: string) => {
    navigate(`/media-planning/edit/${id}`);
    handleMenuClose();
  };

  const handleDelete = (id: string) => {
    setDeletedId(id);
    setOpenDeleteModal(true);
    handleMenuClose();
  };

  const handleDownloadXls = (id: string) => {
    generateXls(id);
    handleMenuClose();
  };

  const limitReached = Object.keys(comparedPlans).length >= 3;

  const columns: GridColDef<Row>[] = [
    {
      field: 'planName',
      headerName: 'Plan name',
      flex: 2.1,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={1}
          width="100%"
          sx={{ opacity: params.row.isLocked ? 0.6 : 1 }}
        >
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.row.planName}
          </Box>
          {params.row.isLocked && (
            <Tooltip
              title={
                <Stack>
                  <span>View only.</span>
                  <span>
                    This plan was created before the recent application update.
                  </span>
                </Stack>
              }
            >
              <InfoOutlineIcon
                sx={{ color: '#447EEB', fontSize: '18px', flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created by',
      flex: 0.7,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ opacity: params.row.isLocked ? 0.6 : 1 }}>
          {params.row.createdBy}
        </Box>
      ),
    },
    {
      field: 'updatedBy',
      headerName: 'Updated by',
      flex: 0.7,
      minWidth: 160,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ opacity: params.row.isLocked ? 0.6 : 1 }}>
          {params.row.updatedBy}
        </Box>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated on',
      flex: 0.6,
      maxWidth: 160,
      sortable: true,
      disableColumnMenu: true,
      valueFormatter: (params) => format(new Date(params.value), 'dd-MM-yyyy'),
      renderCell: (params) => (
        <Box sx={{ opacity: params.row.isLocked ? 0.6 : 1 }}>
          {params.formattedValue}
        </Box>
      ),
    },
    {
      field: 'metaBooked',
      headerName: 'Meta booked',
      width: 160,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Stack flexDirection="row" width="100%">
            <Stack flexDirection="row" gap={1}>
              {params.row.statuses.map((countryStatus) => (
                <CountryStatus
                  countryStatus={countryStatus}
                  key={countryStatus.country}
                  dimmed={params.row.isLocked}
                />
              ))}
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: 'statusInfo',
      headerName: 'Accepted',
      width: 140,
      sortable: true,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Stack flexDirection="row" justifyContent="center">
          {params.row.statusInfo?.current === statuses.ACCEPTED && (
            <LockIconOutlined />
          )}
        </Stack>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          {!comparedPlans[params.row.id] ? (
            <IconButton
              onClick={() =>
                handleAddToComparision(params.row.id, params.row.planName)
              }
              disabled={limitReached}
            >
              <SvgIcon
                width="20"
                height="18"
                viewBox="0 0 20 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.8164 -4.728e-07L8.68359 -3.79572e-07L8.68359 8.63281L10.8164 8.63281L10.8164 -4.728e-07ZM5.68248e-07 13L2.18359 13L2.18359 -9.54479e-08L0 0L5.68248e-07 13ZM4.31641 13L6.5 13L6.5 -2.84124e-07L4.31641 -1.88676e-07L4.31641 13Z"
                  fill={limitReached ? 'currentColor' : '#525F81'}
                />
                <path
                  d="M19.666 10.8672H15.3496V6.5H13.166V10.8672H8.84961V13H13.166V17.3672H15.3496V13H19.666V10.8672Z"
                  fill={limitReached ? 'currentColor' : '#388C2D'}
                />
              </SvgIcon>
            </IconButton>
          ) : (
            <IconButton
              onClick={() => handleRemoveFromComparision(params.row.id)}
            >
              <SvgIcon
                width="20"
                height="13"
                viewBox="0 0 20 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.8164 -4.728e-07L8.68359 -3.79572e-07L8.68359 8.63281L10.8164 8.63281L10.8164 -4.728e-07ZM5.68248e-07 13L2.18359 13L2.18359 -9.54479e-08L0 0L5.68248e-07 13ZM4.31641 13L6.5 13L6.5 -2.84124e-07L4.31641 -1.88676e-07L4.31641 13Z"
                  fill="#525F81"
                />
                <path
                  d="M19.666 13V10.8672H8.84961V13H19.666Z"
                  fill="#F00F00"
                />
              </SvgIcon>
            </IconButton>
          )}

          <IconButton onClick={(event) => handleMenuOpen(event, params.row.id)}>
            <MoreVert />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedRow === params.row.id}
            onClose={handleMenuClose}
          >
            {params.row.statusInfo?.current === statuses.ACCEPTED && (
              <MenuItem onClick={() => handleView(params.row.id)}>
                {t('mediaPlanning.viewPlan')}
              </MenuItem>
            )}
            {getAbilityForAccepting(userRoles) &&
              !params.row.isLocked &&
              (params.row.statusInfo?.current === statuses.BOOKED ||
                (params.row.statusInfo?.current === statuses.SAVED &&
                  params.row.statuses.every((s) => s.status === null))) && (
                <MenuItem onClick={() => handleAcceptPlan(params.row.id)}>
                  {t('mediaPlanning.acceptPlan')}
                </MenuItem>
              )}

            {getAbilityForAccepting(userRoles) &&
              !params.row.isLocked &&
              params.row.statusInfo?.current === statuses.ACCEPTED && (
                <MenuItem onClick={() => handleUnlockPlan(params.row.id)}>
                  {t('mediaPlanning.unlockPlan')}
                </MenuItem>
              )}

            {params.row.statusInfo?.current !== statuses.ACCEPTED && (
              <MenuItem onClick={() => handleEdit(params.row.id)}>
                {params.row.isLocked
                  ? t('mediaPlanning.checkPlan')
                  : t('mediaPlanning.editPlan')}
              </MenuItem>
            )}

            {params.row.statusInfo?.current !== statuses.ACCEPTED && (
              <MenuItem
                onClick={() => handleDelete(params.row.id)}
                disabled={params.row.statuses.some(
                  (countryStatus) =>
                    countryStatus.status === 'reserved' ||
                    countryStatus.status === 'partially-reserved'
                )}
              >
                {t('mediaPlanning.deletePlan')}
              </MenuItem>
            )}

            <MenuItem onClick={() => handleDownloadXls(params.row.id)}>
              {t('mediaPlanning.downloadXls')}
            </MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return { columns, deletePlan, deletedId };
};

export default useMediaPlansTableColumns;
